'use client';

import { prescriptionRecognition } from '@/api/product';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import {
  BasePrescriptionRecognition,
  ILoadPR,
  IPrescriptionRecognition,
} from '@/app/_lens-components/types';
import { getApiPrescriptionType } from '@/app/_lens-components/utils';
import { getEmptyPrescription } from '@/components/PrescriptionFormV2/utils';
import { toast } from '@/components/Toast';
import { useReporting } from '@/hooks/useReporting';
import logger from '@/utils/client-logger';
import { EPrescriptionType } from '@/utils/enums/goods';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { TrackingService } from '../services/TrackingService';
import ResultDialog from './ResultDialog';
import ScanningDialog from './ScanningDialog';
import UploadDialog from './UploadDialog';

export interface ScanPrescriptionOldProps {
  /** 上传弹窗是否打开（由父组件控制，如 onScanClick 时设为 true） */
  showUploadDialog: boolean;
  /** 关闭上传弹窗回调 */
  onCloseUploadDialog: () => void;
  prescriptionType: EPrescriptionType;
  presTypeOptionName: string;
  prLoadAnimationTime: number;
  onLoadPrescription: (data: ILoadPR) => void;
  onFinishScan: () => void;
}

/**
 * 处方识别相关弹窗封装：上传弹窗、扫描中弹窗、结果弹窗及其逻辑
 * 从 PrescriptionRecognition.tsx 抽离，便于复用或对比
 */
export default function ScanPrescriptionOld({
  showUploadDialog,
  onCloseUploadDialog,
  prescriptionType,
  presTypeOptionName,
  prLoadAnimationTime,
  onLoadPrescription,
  onFinishScan,
}: ScanPrescriptionOldProps) {
  const t = useTranslations('lens-process');
  const { trackEvent } = useReporting();
  const { lensGoodsItem, lensGoodsSensorsData, setScanInfo } = useLensProcessStore();

  const trackingService = new TrackingService(
    trackEvent,
    lensGoodsSensorsData,
    prescriptionType,
    presTypeOptionName,
  );

  const [isScanning, setIsScanning] = useState(false);
  const isCancelScanRef = useRef(false);
  const [showResultPop, setShowResultPop] = useState(false);
  const [resultData, setResultData] = useState<BasePrescriptionRecognition>(null);
  const [uploadedS3Url, setUploadedS3Url] = useState<string>('');

  const handleFileUpload = async (fileUrl: string) => {
    try {
      if (fileUrl) {
        trackingService.prescriptionEnterPageUploadSuccessResult();
        isCancelScanRef.current = false;
        startScan(fileUrl);
      } else {
        toast.error(t('uploadFailed'));
      }
    } catch (error) {
      toast.error(t('uploadFailed'));
      logger.error('Failed to upload prescription image', error);
    }
  };

  const startScan = async (fileUrl: string) => {
    try {
      setIsScanning(true);
      const response = await prescriptionRecognition({
        prescription_type: getApiPrescriptionType(prescriptionType),
        product_id: lensGoodsItem?.product_id,
        prescription_img: fileUrl,
      });
      if (isCancelScanRef.current) {
        return;
      }

      if (response.code !== 200) {
        toast.error(response.msg || t('uploadFailed'));
        setIsScanning(false);
      } else {
        setUploadedS3Url(fileUrl);
        onFinishScan();
        setIsScanning(false);
        const { prescription_correction: { type } = {} } = response.data;
        setResultData(response.data);

        let prescription_scan_result_type = 'failure';

        if (type === 1) {
          prescription_scan_result_type = 'success';
          handleScanFinish(response.data, false, fileUrl);
        } else if ([3, 5].includes(type)) {
          setShowResultPop(true);
          prescription_scan_result_type = type === 5 ? 'correction' : 'failure';
          if (type === 5) {
            trackingService.prescriptionEnterPageScanPopupChangeExp();
          } else if (type === 3) {
            trackingService.prescriptionEnterPageScanPopupFailExp();
          }
        } else {
          toast.error(t('uploadFailed'));
          prescription_scan_result_type = 'failure';
        }

        trackingService.prescriptionEnterPageScanParamResult(prescription_scan_result_type);
      }
    } catch (error) {
      if (isCancelScanRef.current) {
        return;
      }
      setIsScanning(false);
      toast.error(t('uploadFailed'));
      logger.error('Failed to scan prescription', error);
    }
  };

  const handleScanFinish = (
    data: IPrescriptionRecognition,
    ignoreAdd = false,
    imageUrl?: string,
  ) => {
    onCloseUploadDialog();
    setShowResultPop(false);
    logger.log('handleScanFinish___', data);

    const { option, prescription_type: presType, record_id } = data;

    let finalOption = { ...option };
    if (ignoreAdd) {
      finalOption = {
        ...finalOption,
        od_add: '0.00',
        os_add: '0.00',
      };
    }

    const scannedOption = { ...getEmptyPrescription(), ...finalOption };
    const finalImageUrl = imageUrl || uploadedS3Url;

    setScanInfo({
      recordId: record_id,
      imageUrl: finalImageUrl,
    });

    onLoadPrescription({
      prescription: scannedOption,
      presType:
        presType === EPrescriptionType.READING_GLASSES
          ? EPrescriptionType.PRESCRIPTION_READING
          : presType,
      record_id,
      prescriptions_image: finalImageUrl,
    });

    trackingService.prescriptionScanSuccessPageView(presType || '');
  };

  const handleCancelScan = () => {
    setIsScanning(false);
    isCancelScanRef.current = true;
  };

  const handleCorrectionConfirm = (selectedOption: { value: string; type: string }) => {
    if (selectedOption.type === 'ignoreAdd') {
      handleScanFinish(resultData, true);
      trackingService.prescriptionEnterPageScanPopupChangeNextClick();
    } else if (selectedOption.type === 'changePres') {
      handleScanFinish({
        ...resultData,
        prescription_type: selectedOption.value,
      });
      trackingService.prescriptionEnterPageScanPopupChangeNextClick(selectedOption.value);
    }
  };

  const handleClickUpload = (buttonType: 'Upload' | 'Photo') => {
    trackingService.prescriptionEnterPageScanPopupClick(buttonType);
  };

  return (
    <>
      {/* 上传弹窗 */}
      <UploadDialog
        isOpen={showUploadDialog}
        onClose={onCloseUploadDialog}
        onFileUpload={handleFileUpload}
        onClickUpload={handleClickUpload}
      />

      {/* 扫描中弹窗 */}
      <ScanningDialog
        isOpen={isScanning}
        onCancel={handleCancelScan}
        animationTime={prLoadAnimationTime}
      />

      {/* 结果弹窗 */}
      <ResultDialog
        isOpen={showResultPop}
        onClose={() => setShowResultPop(false)}
        resultData={resultData}
        uploadedS3Url={uploadedS3Url}
        onCorrectionConfirm={handleCorrectionConfirm}
      />
    </>
  );
}
